/// <reference path="./CreateProduct/interface.d.ts" />
import { v4 as uuidv4 } from "uuid";
import { backendUrl } from "config";
import {
  ItemProductByIdResponse,
  PriceProductByIdResponse,
  ProductProductById,
} from "services/interface";
import { toMerchantIdArray } from "utils";

/** ต่อท้ายชื่อสินค้าที่ถูกคัดลอก */
export const CLONE_NAME_SUFFIX = " (สำเนา)";

export interface CloneFormState {
  variants: VariantsInput;
  prices: PricesInput;
  attribute: any[];
  /** ค่าที่เอาไปใส่ form ของหน้าเพิ่มสินค้าด้วย form.setFieldsValue */
  fields: Record<string, any>;
}

/**
 * ราคาในฟอร์ม (local state) — GET /product/{id} คืนบางฟิลด์เป็น string จึงต้อง coerce
 * (ชุดเดียวกับที่หน้าแก้ไขสินค้าทำใน UpdateProduct/index.tsx)
 * ผูกกับ SKU ใหม่ด้วย variant_1_item_id = id ที่เพิ่งสร้าง และใช้ id ใหม่ (ไม่ใช้ id เดิมจาก DB)
 */
const toClonePrice = (price: PriceProductByIdResponse, itemId: string): Price => ({
  id: uuidv4(),
  variant_1_item_id: itemId,
  variant_2_item_id: "",
  factory_price: Number(price.factory_price),
  wholesale_price: Number(price.wholesale_price),
  retail_price: Number(price.retail_price),
  vip_price: Number(price.vip_price),
  base_factory_price: Number(price.base_factory_price ?? 0),
  base_wholesale_price: Number(price.base_wholesale_price ?? 0),
  base_retail_price: Number(price.base_retail_price ?? 0),
  base_vip_price: Number(price.base_vip_price ?? 0),
  transport_price: Number(price.transport_price ?? 0),
  api_transport_carrier_code: price.api_transport_carrier_code || undefined,
  api_transport_logo: price.api_transport_logo || undefined,
  api_transport_price: Number(price.api_transport_price ?? 0),
  add_on_api_transport_price: Number(price.add_on_api_transport_price ?? 0),
  percent_payment_gateway: Number(price.percent_payment_gateway ?? 3),
  payment_getway: Number(price.payment_getway ?? 0),
  commistion_lv1: price.commistion_lv1 ?? undefined,
  commistion_lv2: price.commistion_lv2 ?? undefined,
  commistion_lv3: price.commistion_lv3 ?? undefined,
  commistion_lv4: price.commistion_lv4 ?? undefined,
  commistion_lv5: price.commistion_lv5 ?? undefined,
  total_commission: price.total_commission ?? undefined,
  unit_factory_price: String(price.unit_factory_price ?? "0"),
  unit_wholesale_price: String(price.unit_wholesale_price ?? "0"),
  unit_retail_price: String(price.unit_retail_price ?? "0"),
  unit_vip_price: String(price.unit_vip_price ?? "0"),
  ...(price.quantity_per_unit != null ? { quantity_per_unit: Number(price.quantity_per_unit) } : {}),
  ...(price.product_unit ? { product_unit: price.product_unit } : {}),
  // เก็บ master_sku_tag ไว้ให้ radio "ประเภทการขาย" ใน modal SKU pre-fill ได้
  ...((price as any).sku_tags ? { sku_tags: (price as any).sku_tags } : {}),
});

/** SKU ในฟอร์ม — id ใหม่, ล้างรหัสสินค้า, ไม่คัดลอกข้อมูลสต็อก */
const toCloneItem = (
  item: ItemProductByIdResponse,
  itemId: string,
  price?: PriceProductByIdResponse
): VariantItem => ({
  id: itemId,
  name: item.name,
  image_id: item.image_id,
  image_url: item.image_url,
  product_code: "", // ล้างรหัสสินค้าให้กรอกใหม่ (เหมือนโหมดคัดลอก SKU)
  product_capacity: item.product_capacity,
  product_quantity: item.product_quantity,
  lead_time: item.lead_time,
  preparation_time: item.preparation_time || 1,
  width: item.width || 0,
  length: item.length || 0,
  height: item.height || 0,
  weight: item.weight || 0,
  packaging_type_id: item.packaging_type_id || null,
  // ถ้า API ไม่ส่ง shipping_method มา ให้อนุมานจากมิติ/น้ำหนัก (มีข้อมูลพัสดุ = ส่งกับแบ่งเบา)
  shipping_method:
    item.shipping_method ??
    ((item.width || 0) > 0 ||
    (item.length || 0) > 0 ||
    (item.height || 0) > 0 ||
    (item.weight || 0) > 0
      ? "bangbow"
      : "self"),
  is_active: item.is_active ?? true,
  is_draft: item.is_draft || false,
  is_cod: item.is_cod ?? false,
  merchants: toMerchantIdArray(item.merchants),
  merchant_id: item.merchant_id || "",
  // ประเภทการขาย (sale_channel) ผูกอยู่กับ price.sku_tags ที่ API ส่งมา
  sku_tag_ids: (((price as any)?.sku_tags ?? []) as SkuTagItem[]).map((tag) => tag.id),
  partner_transport_id: item.partner_transport_id || null,
  partner_transport_brand_id: item.partner_transport_brand_id || null,
  partner_transport_brand_name: item.partner_transport_brand_name || null,
  partner_transport_brand_logo: item.partner_transport_brand_logo || null,
  // สต็อกไม่ถูกคัดลอก — POST /product ไม่รับข้อมูลคลัง ต้องไปตั้งใหม่หลังบันทึก
  is_stock: false,
  stock: { stock_quantity: 0, holding_quantity: 0, stock_balance: 0 },
});

/**
 * แปลงสินค้าจาก GET /product/{id} เป็นค่าเริ่มต้นของ "หน้าเพิ่มสินค้า"
 * เพื่อให้ผู้ใช้ตรวจ/แก้ก่อนกดบันทึก (ยังไม่มีการเรียก POST /product ตอนนี้)
 *
 * - SKU ทุกตัวได้ id ใหม่ (uuid) แบบเดียวกับ SKU ที่สร้างในโหมด create
 *   และแตกเป็น 1 item ต่อ 1 variant ตามระบบ Single-Step SKU
 * - รูปใช้ image id เดิมได้ ไม่ต้องอัปโหลดซ้ำ
 */
export const buildCloneFormState = (product: ProductProductById): CloneFormState => {
  const variants: VariantsInput = [];
  const prices: PricesInput = [];

  product.variants.forEach((variant) => {
    variant.items.forEach((item) => {
      const itemId = uuidv4();
      const price = product.prices.find((p) => p.variant_1_item_id === item.id);

      variants.push({
        id: uuidv4(),
        name: variant.name,
        capacity: item.product_capacity,
        items: [toCloneItem(item, itemId, price)],
      });

      if (price) prices.push(toClonePrice(price, itemId));
    });
  });

  return {
    variants,
    prices,
    attribute: product.properties ?? [],
    fields: {
      name: `${product.name}${CLONE_NAME_SUFFIX}`,
      description: product.description,
      category_id: product.category_id,
      recomended_retail: product.recomended_retail,
      main_images: (product.main_images ?? []).map((imageId: string) => ({
        uid: imageId,
        name: product.name,
        status: "done",
        id: imageId,
        url: `${backendUrl}images/${imageId}`,
      })),
      is_active: product.is_active, // ต้นฉบับเปิดขายอยู่ → สำเนาเปิดขายด้วย
      is_recommend: product.is_recommend,
      is_dead_stock: product.is_dead_stock ?? false, // ตามสินค้าต้นฉบับ
      retail_type: (product.retail_type ?? []).map((type: number) => type.toString()),
      video_url: product.video_url,
      tag_list: product.tag_list,
      sg_important_advice: product.sg_important_advice,
      sg_recommended_shop: product.sg_recommended_shop,
      sg_frequently_qna: product.sg_frequently_qna,
      sg_sales_income: product.sg_sales_income,
      sg_invite_sale: product.sg_invite_sale,
      sg_waiting_time: product.sg_waiting_time,
    },
  };
};
